import React, {useState, useCallback, useEffect} from 'react';
import {HSLColor} from 'polyphony/colors';
import {useRefWithListeners, useHotkeys} from 'polyphony/hooks';
import {prefixCSS} from 'polyphony/css';
import {combineRefs} from 'polyphony/util';

function drawTrack(canvas, makeColor) {
    let ctx = canvas.getContext('2d');
    let w = canvas.clientWidth;
    let h = canvas.clientHeight;

    // create gradient
    let gradC = ctx.createLinearGradient(0, h, w, h);

    for (let i = 0; i <= 1; i += 0.01) {
        gradC.addColorStop(i, makeColor(i));
    }

    // create background
    let nRow = h / 10;
    let nCol = w / 10;
    let blockWidth = canvas.width / nCol;
    let blockHeight = canvas.height / nRow;

    for (let i = 0; i < nRow; ++i) {
        for (let j = 0, col = nCol / 2; j < col; ++j) {
            ctx.rect(2 * j * blockWidth + (i % 2 ? 0 : blockWidth),
                     i * blockHeight,
                     blockWidth,
                     blockHeight);
        }
    }

    // draw background
    ctx.fill();

    // draw gradient
    ctx.fillStyle = gradC;
    ctx.fillRect(0, 0, w, h);
}

const trackStyles = {
    container: prefixCSS({
        position: 'relative',
        height: 30,
        width: '100%',
        margin: "20px 0",
        borderRadius: 5
    }),
    canvas: prefixCSS({
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: 5
    }),
    scrubber: prefixCSS({
        position: 'absolute',
        height: 'calc(100% + 10px)',
        marginTop: -5,
        width: 5,
        background: 'black',
        pointerEvents: 'none',
        borderRadius: 5,
        cursor: 'pointer'
    })
};

const Track = ({percent, makeColor, onChange, label}) => {
    // Aria Best Practices
    // https://www.w3.org/TR/wai-aria-practices/examples/slider/slider-1.html

    const canvas = useRefWithListeners({
        resize: e => drawTrack(e.target, makeColor)
    }, [makeColor]);

    const trackUpdated = e => {
        if (e.buttons > 0) {
            onChange(e.offsetX / e.target.clientWidth);
        }
    };

    const container = useRefWithListeners(onChange ? {
        mousemove: trackUpdated,
        mousedown: trackUpdated
    } : {}, [onChange]);

    useEffect(() => {
        if (canvas.current) {
            drawTrack(canvas.current, makeColor);
        }
    }, [makeColor]);

    const decrementValue = () => onChange(percent - 0.01);
    const incrementValue = () => onChange(percent + 0.01);

    const ref = useHotkeys({
        ArrowRight: incrementValue,
        ArrowLeft: decrementValue,
        ArrowUp: incrementValue,
        ArrowDown: decrementValue,
        PageUp: () => onChange(percent + 0.1),
        PageDown: () => onChange(percent - 0.1),
        Home: () => onChange(0),
        End: () => onChange(1.0)
    }, [onChange]);

    return <div style={trackStyles.container}
                ref={combineRefs([ref, container])}
                role='slider'
                aria-valuemin="0"
                aria-valuenow={`${Math.trunc(percent * 100)}%`}
                aria-valuemax="100%"
                tabIndex='0%'
                aria-label={label}>
            <canvas role="none" key="canvas" ref={canvas} height={30} width={100} style={trackStyles.canvas} />
            <div role="none" style={{...trackStyles.scrubber, left: `${percent * 100}%`}} />
        </div>;
};

const ColorPicker = ({color: initialColor, // string
                      onChange}) => {
    let [color, setColor] = useState(() => {
        let c = HSLColor.parseString(initialColor);
        if (!c) return HSLColor.parseString('transparent');
        return c;
    });
    let [text, setText] = useState(() => color.toHexString());

    const changeHue = useCallback(h => {
        const c = color.withHue(h);
        setColor(c);
        setText(c.toHexString());
        if (onChange) onChange(c.toHslString());
        color = c;
    }, [color, onChange, setColor, setText]);

    const changeSaturation = useCallback(s => {
        const c = color.withSaturation(s);
        setColor(c);
        setText(c.toHexString());
        if (onChange) onChange(c.toHslString());
        color = c;
    }, [color, onChange, setColor, setText]);

    const changeLightness = useCallback(l => {
        const c = color.withLightness(l);
        setColor(c);
        setText(c.toHexString());
        if (onChange) onChange(c.toHslString());
        color = c;
    }, [color, onChange, setColor, setText]);

    const changeAlpha = useCallback(a => {
        const c = color.withAlpha(a);
        setColor(color);
        setText(c.toHexString());
        if (onChange) onChange(c.toHslString());
        color = c;
    }, [color, onChange, setColor, setText]);

    const changeText = useCallback(e => {
        let text = e.target.value;
        setText(text);

        let c = HSLColor.parseString(e.target.value);
        if (c) {
            setColor(c);
            if (onChange) onChange(c.toHslString());
            color = c;
        }
    }, [color, onChange, setColor, setText]);

    const makeHue = useCallback(i => color.withHue(i / 360).toHslString(), [color]);
    const makeSaturation = useCallback(i => color.withSaturation(i / 100).toHslString(), [color]);
    const makeLightness = useCallback(i => color.withLightness(i / 100).toHslString(), [color]);
    const makeAlpha = useCallback(i => color.withAlpha(i / 100).toHslString(), [color]);

    return (
        <>
            <Track percent={color.h} onChange={changeHue} makeColor={makeHue}/>
            <Track percent={color.s} onChange={changeSaturation} makeColor={makeSaturation}/>
            <Track percent={color.l} onChange={changeLightness} makeColor={makeLightness}/>
            <Track percent={color.a} onChange={changeAlpha} makeColor={makeAlpha}/>
            <div role="none" style={prefixCSS({display: 'flex', width: '100%'})}>
                <div role="figure" style={{height: 34, width: 34, borderRadius: 17, marginRight: 5, backgroundColor: color.toHslString()}} />
                <input style={{height: 34, width: 'calc(100% - 39px)'}} type='text' onChange={changeText} value={text} />;
            </div>
        </>
    );
};

ColorPicker.displayName = 'ColorPicker';

export default ColorPicker;

